import React, { useEffect, useState } from 'react';
import { FaSave } from 'react-icons/fa';

const DeliveryServices = () => {
  const [deliveryServices, setDeliveryServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalData, setModalData] = useState(null); // Data for Add/Edit modal
  const [deleteId, setDeleteId] = useState(null); // ID of delivery service to delete
  const [editMode, setEditMode] = useState(false); // Edit mode toggle
  const apiUrl = process.env.REACT_APP_BASE_URL;

  // Fetch all delivery services
  const fetchDeliveryServices = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiUrl}/api/delivery-service`);
      if (!response.ok) throw new Error('Failed to fetch delivery services');
      const data = await response.json();
      setDeliveryServices(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to fetch data');
      setDeliveryServices([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeliveryServices();
  }, []);

  // Save or update a delivery service
  const handleSave = async (service) => {
    const method = editMode ? 'PUT' : 'POST';
    const url = editMode
      ? `${apiUrl}/api/delivery-service/${service.id}`
      : `${apiUrl}/api/delivery-service`;

    try {
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(service),
      });
      if (!response.ok) throw new Error('Error saving delivery service');
      await fetchDeliveryServices();
      setModalData(null); // Close modal
    } catch (err) {
      setError('Error saving delivery service. Please try again.');
    }
  };

  // Delete a delivery service
  const handleDelete = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/delivery-service/${deleteId}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Error deleting delivery service');
      await fetchDeliveryServices();
      setDeleteId(null); // Close delete confirmation modal
    } catch (err) {
      setError('Error deleting delivery service. Please try again.');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Delivery Services</h2>
        <button
          className="btn btn-primary"
          onClick={() => {
            setEditMode(false);
            setModalData({ name: '', pattern: '' }); // Open modal with empty data
          }}
        >
          Add Delivery Service
        </button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : error ? (
        <p className="text-red-500">{error}</p>
      ) : deliveryServices.length === 0 ? (
        <p>No delivery services available.</p>
      ) : (
        <table className="table w-full border-collapse border border-gray-300">
          <thead>
            <tr>
              <th className="border border-gray-300 bg-base-200">Name</th>
              <th className="border border-gray-300 bg-base-200">Tracking Pattern</th>
              <th className="border border-gray-300 bg-base-200">Actions</th>
            </tr>
          </thead>
          <tbody>
            {deliveryServices.map((service) => (
              <tr key={service.id}>
                <td>{service.name}</td>
                <td>{service.pattern}</td>
                <td className="flex gap-2">
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => {
                      setEditMode(true);
                      setModalData(service); // Open modal with delivery service data
                    }}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => setDeleteId(service.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Add/Edit Modal */}
      {modalData && (
        <dialog className="modal modal-open bg-gray-50">
          <form
            className="modal-box"
            onSubmit={(e) => {
              e.preventDefault();
              handleSave(modalData);
            }}
          >
            <h3 className="font-bold text-lg">
              {editMode ? 'Edit Delivery Service' : 'Add Delivery Service'}
            </h3>
            <div className="form-control">
              <label htmlFor="name" className="label-text">
                Name
              </label>
              <input
                type="text"
                name="name"
                value={modalData.name || ''}
                onChange={(e) =>
                  setModalData((prev) => ({ ...prev, name: e.target.value }))
                }
                className="input input-bordered"
                required
              />
            </div>
            <div className="form-control">
              <label htmlFor="pattern" className="label-text">
                Tracking Pattern
              </label>
              <input
                type="text"
                name="pattern"
                value={modalData.pattern || ''}
                onChange={(e) =>
                  setModalData((prev) => ({ ...prev, pattern: e.target.value }))
                }
                className="input input-bordered"
                required
              />
            </div>
            <div className="modal-action">
              <button
                type="button"
                className="btn"
                onClick={() => setModalData(null)} // Close modal
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                <FaSave />
                {editMode ? 'Update' : 'Save'}
              </button>
            </div>
          </form>
        </dialog>
      )}

      {/* Delete Confirmation Modal */}
      {deleteId && (
        <dialog className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">Confirm Deletion</h3>
            <p>Are you sure you want to delete this delivery service? This action cannot be undone.</p>
            <div className="modal-action">
              <button
                className="btn"
                onClick={() => setDeleteId(null)} // Close modal
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </dialog>
      )}
    </div>
  );
};

export default DeliveryServices;
